import { RowSelectionState } from '@tanstack/react-table';

import { FC } from 'react';

import SelectedRunsActions from '@/components/RunsTable/SelectedRunsActions';
import { BulkActionResult } from '@/components/RunsTable/types';
import useToast from '@/components/Toast/useToast';
import { useGroupRuns, useRunsQuery } from '@/hooks/useSwr';
import { RunGroupSchema, RunSchema } from '@/types/schema';

type TProps = {
  sessionId: string;
  rowSelection: RowSelectionState;
  setRowSelection: (rowSelection: RowSelectionState) => void;
  selectedConversations: RunGroupSchema[];
  setBulkActionResult: (result: BulkActionResult) => void;
  limit?: number;
};

const SelectedConversationsActions: FC<TProps> = ({
  sessionId,
  rowSelection,
  setRowSelection,
  setBulkActionResult,
  limit = 50,
}) => {
  const { createToast } = useToast();
  const allThreadsIds = Object.keys(rowSelection);
  const { data: groupRuns, error: runGroupsError } = useGroupRuns({
    session_id: sessionId,
    group_by: 'conversation',
    filter: `in(thread_id, [${allThreadsIds
      .map((threadId) => `"${threadId}"`)
      .join(',')}])`,
    limit,
  });

  if (runGroupsError) {
    createToast({
      title: 'Error fetching thread data',
      description: runGroupsError.message,
      error: true,
    });
  }

  const latestRunIds: string[] =
    groupRuns?.groups
      .map((group) => group.last_run_id)
      .filter((x): x is string => x != null) ?? [];

  const { data: runsPages, error } = useRunsQuery(
    latestRunIds.length
      ? {
          session: [sessionId],
          order: 'desc',
          id: latestRunIds,
          select: ['id', 'thread_id', 'trace_id'],
          limit,
        }
      : null
  );

  if (error && !runGroupsError) {
    createToast({
      title: 'Error fetching runs',
      description: error.message,
      error: true,
    });
  }

  const traceIds = runsPages?.runs.map((run) => run.trace_id);

  const { data: rootRunsPages, error: rootRunsError } = useRunsQuery(
    traceIds?.length
      ? {
          session: [sessionId],
          is_root: true,
          order: 'desc',
          id: traceIds,
          select: ['id', 'thread_id', 'trace_id'],
          limit,
        }
      : null
  );

  if (error && !runGroupsError && !rootRunsError) {
    createToast({
      title: 'Error fetching traces',
      description: error.message,
      error: true,
    });
  }

  const allRuns = rootRunsPages?.runs;

  const selectedRuns = Object.values(
    allRuns?.reduce((runs: Record<string, RunSchema>, run: RunSchema) => {
      // Last run in thread is the one we want to add to the dataset/annotation queue
      if (run.thread_id && !runs[run.thread_id]) {
        runs[run.thread_id] = run;
      }
      return runs;
    }, {}) ?? {}
  );

  const runsSelection = Object.fromEntries(
    Object.keys(rowSelection).map((threadId) => [
      selectedRuns.find((run) => run.thread_id === threadId)?.id,
      true,
    ])
  );

  const propagateRowSelection = (runSelection: RowSelectionState) => {
    setRowSelection(
      Object.fromEntries(
        Object.keys(runSelection).map((runId) => [
          selectedRuns.find((run) => run.id === runId)?.extra?.metadata
            .thread_id,
          true,
        ])
      )
    );
  };

  return (
    <SelectedRunsActions
      rowSelection={runsSelection}
      setRowSelection={propagateRowSelection}
      setBulkActionResult={setBulkActionResult}
      selectedRuns={selectedRuns}
      // Other components will appropriately tack on an "s" for pluralization
      runDisplayText={`final ${
        Object.keys(runsSelection).length === 1 ? 'run' : 'runs'
      } in the selected thread`}
    />
  );
};

export default SelectedConversationsActions;
